'use client';

import { clsx } from 'clsx';
import { Dispatch, RefObject, SetStateAction } from 'react';

import { UniversalImage } from '@monoimin/atoms';
import { FullscreenCarousel } from '@monoimin/molecules/carousel';
import { CarouselButton } from '@monoimin/molecules/carousel/CarouselButton';

interface ImageGalleryViewProps {
  items: Array<{ alt: string; src: string; priority?: boolean }>;
  className?: string;
  openModal: boolean;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
  activePageIndex: number;
  prev: () => void;
  next: () => void;
  goTo: (index: number) => void;
  scrollRef: (el: HTMLElement | null) => void;
  buttonContainerRef: RefObject<HTMLDivElement>;
  buttonsRef: RefObject<HTMLDivElement>[];
}

export const ImageGalleryView = ({
  items,
  className,
  openModal,
  setOpenModal,
  activePageIndex,
  prev,
  next,
  goTo,
  scrollRef,
  buttonContainerRef,
  buttonsRef,
}: ImageGalleryViewProps) => {
  return (
    <>
      <FullscreenCarousel
        setIsOpen={setOpenModal}
        isOpen={openModal}
        initialActive={activePageIndex}
        slides={items.map((item) => {
          return (
            <UniversalImage
              pattern='fill'
              key={item.src}
              alt={item.alt}
              src={item.src}
              className='object-contain object-center'
              containerClassName='aspect-video w-full'
              sizes='(max-width: 1264px) calc(100vw - 40px), 1224px'
              draggable={false}
            />
          );
        })}
      />
      <div
        className={clsx(
          'image-gallery flex flex-col gap-3 overflow-hidden w-full',
          className
        )}
      >
        <div className='flex relative aspect-video group'>
          {Boolean(activePageIndex) && (
            <CarouselButton
              onClick={prev}
              className='absolute top-1/2 -translate-y-1/2 left-5 bg-black/40 opacity-0 group-hover:opacity-100 hover:bg-black/100 duration-150 easy-in-out'
              direction='left'
            />
          )}
          {activePageIndex < items.length - 1 && (
            <CarouselButton
              onClick={next}
              className='absolute top-1/2 -translate-y-1/2 right-5 bg-black/40 opacity-0 group-hover:opacity-100 hover:bg-black/100 duration-150 easy-in-out'
              direction='right'
            />
          )}
          <div
            ref={scrollRef}
            className='overflow-x-auto flex snap-x snap-mandatory scrollbar-hide relative w-full h-full'
          >
            {items.map((item) => {
              return (
                <UniversalImage
                  pattern='fill'
                  key={item.src}
                  alt={item.alt}
                  src={item.src}
                  onClick={() => setOpenModal(true)}
                  className='object-contain object-center'
                  containerClassName='image-gallery-image aspect-video snap-center snap-always shrink-0 cursor-pointer'
                  sizes='(max-width: 1264px) calc(100vw - 40px), 1224px'
                  priority={item.priority}
                  draggable={false}
                  data-testid="image-gallery-trigger"
                />
              );
            })}
          </div>
        </div>
        <div
          ref={buttonContainerRef}
          className='flex w-full gap-4 overflow-x-scroll snap-x snap-mandatory scrollbar-hide py-2 px-1 scroll-smooth'
        >
          {items.map((item, index) => {
            return (
              <UniversalImage
                pattern='fill'
                key={item.src}
                alt={item.alt}
                src={item.src}
                ref={buttonsRef[index]}
                onClick={() => goTo(index)}
                className='object-contain object-center'
                containerClassName={clsx(
                  'image-gallery-image w-20 h-20 md:w-36 md:h-24 shrink-0 cursor-pointer outline outline-2 outline-offset-2 duration-100 ease-out scroll-mx-3',
                  activePageIndex === index
                    ? 'outline-blue'
                    : 'outline-transparent'
                )}
                sizes='144px'
                priority={item.priority}
                draggable={false}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};
